/**
 * useOAuth Hook
 * Manages OAuth provider linking/unlinking for the Settings page.
 */

import { useAuth } from '@contexts/AuthContext.tsx';
import type {
  LinkedOAuthProvider,
  OAuthProvider,
  OAuthProvidersResult,
} from '@parlay-picker/shared/oauth.ts';
import { useCallback, useEffect, useState } from 'react';

export interface UseOAuthResult {
  providers: LinkedOAuthProvider[];
  hasPassword: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  linkProvider: (
    provider: OAuthProvider,
    idToken: string,
    password: string,
  ) => Promise<{ success: boolean; error?: string }>;
  unlinkProvider: (provider: OAuthProvider) => Promise<{ success: boolean; error?: string }>;
  addPassword: (
    password: string,
    confirmPassword: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

export function useOAuth(): UseOAuthResult {
  const { authFetch } = useAuth();
  const [providers, setProviders] = useState<LinkedOAuthProvider[]>([]);
  const [hasPassword, setHasPassword] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch('/api/auth/oauth/providers');
      if (res.ok) {
        const data: OAuthProvidersResult = await res.json();
        setProviders(data.providers);
        setHasPassword(data.hasPassword);
        setError(null);
      }
    } catch (_err) {
      setError('Failed to load connected accounts');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const linkProvider = useCallback(
    async (provider: OAuthProvider, idToken: string, password: string) => {
      try {
        const res = await authFetch('/api/auth/oauth/link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider, idToken, password }),
        });
        const data = await res.json();
        if (!res.ok) {
          return { success: false, error: data.error || 'Failed to link account' };
        }
        await refresh();
        return { success: true };
      } catch {
        return { success: false, error: 'Failed to link account' };
      }
    },
    [authFetch, refresh],
  );

  const unlinkProvider = useCallback(
    async (provider: OAuthProvider) => {
      try {
        const res = await authFetch('/api/auth/oauth/unlink', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider }),
        });
        const data = await res.json();
        if (!res.ok) {
          return { success: false, error: data.error || 'Failed to unlink account' };
        }
        await refresh();
        return { success: true };
      } catch {
        return { success: false, error: 'Failed to unlink account' };
      }
    },
    [authFetch, refresh],
  );

  const addPassword = useCallback(
    async (password: string, confirmPassword: string) => {
      try {
        const res = await authFetch('/api/auth/add-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password, confirmPassword }),
        });
        const data = await res.json();
        if (!res.ok) {
          return { success: false, error: data.error || 'Failed to add password' };
        }
        await refresh();
        return { success: true };
      } catch {
        return { success: false, error: 'Failed to add password' };
      }
    },
    [authFetch, refresh],
  );

  return {
    providers,
    hasPassword,
    loading,
    error,
    refresh,
    linkProvider,
    unlinkProvider,
    addPassword,
  };
}
